import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router"; // Add useLocation
import { motion } from "framer-motion";
import { LockKeyhole, ShieldCheck, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import AuthSplitLayout from "../../components/auth/AuthSplitLayout";
import { resetConsumerPassword } from "../../api/userAuthApi";
import showToast from "../../utils/toast";

const getPasswordStrength = (password) => {
  if (!password?.length) return { level: 0, label: "" };
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  const level = Math.min(4, score);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  return { level, label: labels[level] };
};

const strengthColors = ["bg-slate-200", "bg-red-400", "bg-amber-400", "bg-lime-400", "bg-emerald-500"];

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#34846E] focus:ring-4 focus:ring-[#34846E]/15";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  // Extract token and email from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlToken = queryParams.get('token');
    const urlEmail = queryParams.get('email');

    console.log("Extracted token from URL:", urlToken);
    
    if (urlToken) {
      setToken(urlToken);
    } else {
      showToast.error("No reset token found. Please request a new password reset.");
    }
    
    if (urlEmail) {
      setEmail(urlEmail);
    }
  }, [location.search]);

  // Calculate isValid properly
  const isValid = useMemo(() => {
    return newPassword.length >= 8 &&
      newPassword === confirmPassword &&
      strength.level >= 2;
  }, [newPassword, confirmPassword, strength.level]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      showToast.error("Invalid or expired token. Please try resetting your password again.");
      return;
    }

    if (!isValid) {
      showToast.error("Please ensure your password meets all requirements and matches the confirmation.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the API with the token from URL
      await resetConsumerPassword({
        token: token,
        password:newPassword,
        password_confirmation: newPassword,
        user_type: "user",
        email: email // Include email if your API requires it
      });

      showToast.success("Your password has been reset successfully. You can now sign in with your new password.");

      // Clear form and redirect after 2 seconds
      setTimeout(() => {
        navigate("/signin");
      }, 2000);

    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to reset password. Please try again.";
      showToast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Return the JSX component
  return (
    <AuthSplitLayout
      title="Set a new password"
      subtitle="Create a strong password to secure your account and continue your InfoBoxx experience."
      sideBadge="PASSWORD RESET"
      sideTitle="A safer account starts with stronger credentials."
      sideDescription="Use a unique password with enough length and complexity to improve account protection."
      sideBullets={[
        "Encrypted credential handling",
        "Security-first account recovery",
        "Frictionless completion flow",
      ]}
      footer={
        <p>
          Back to{" "}
          <Link to="/signin" className="font-semibold text-[#1f6c57] hover:text-[#184d3f]">
            sign in
          </Link>
        </p>
      }
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        {/* New Password */}
        <label className="pt-2 space-y-2">
          <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">New password</span>
          <div className="relative">
            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" size={17} />
            <input
              className={`pr-10 pl-10 ${inputClassName}`}
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword((p) => !p)}
              className="absolute right-3 top-1/2 p-1 rounded-lg transition -translate-y-1/2 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              aria-label={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {newPassword && (
            <div className="space-y-1.5 pt-2">
              <div className="flex gap-1.5 h-2 w-full rounded-full overflow-hidden bg-slate-200">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={`flex-1 rounded-full transition-colors duration-300 ${i <= strength.level ? strengthColors[strength.level] : "bg-slate-200"}`}
                  />
                ))}
              </div>
              <p className="text-xs font-medium text-slate-500">
                Strength:{" "}
                <span
                  className={
                    strength.label === "Strong"
                      ? "text-emerald-600"
                      : strength.label === "Good"
                        ? "text-lime-600"
                        : strength.label === "Fair"
                          ? "text-amber-600"
                          : "text-red-600"
                  }
                >
                  {strength.label || "—"}
                </span>
              </p>
            </div>
          )}
        </label>

        {/* Confirm Password */}
        <label className="space-y-1.5 pt-2">
          <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">Confirm password</span>
          <div className="relative">
            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" size={17} />
            <input
              className={`pr-10 pl-10 ${inputClassName}`}
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Repeat new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((p) => !p)}
              className="absolute right-3 top-1/2 p-1 rounded-lg transition -translate-y-1/2 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {confirmPassword && (
            <p className={`flex items-center gap-1.5 text-xs font-medium ${newPassword === confirmPassword ? "text-emerald-600" : "text-red-600"}`}>
              {newPassword === confirmPassword ? <CheckCircle size={14} /> : <XCircle size={14} />}
              {newPassword === confirmPassword ? "Passwords match" : "Passwords don't match"}
            </p>
          )}
        </label>

        <div className="rounded-xl border border-[#34846E]/25 bg-[#34846E]/7 p-3 text-sm text-[#215948]">
          <p className="inline-flex gap-2 items-start">
            <ShieldCheck size={18} className="mt-0.5 shrink-0" />
            Use at least 8 characters and include letters, numbers, and a symbol for stronger protection.
          </p>
        </div>

        <motion.button
          whileHover={isValid && !isSubmitting ? { scale: 1.01 } : {}}
          whileTap={isValid && !isSubmitting ? { scale: 0.99 } : {}}
          type="submit"
          disabled={!isValid || isSubmitting}
          className={`w-full rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-lg transition ${isValid && !isSubmitting
            ? "bg-gradient-to-r from-[#1f6d57] to-[#34846E] shadow-emerald-900/20 hover:from-[#195846] hover:to-[#2d7662] cursor-pointer"
            : "bg-slate-300 cursor-not-allowed"
            }`}
        >
          {isSubmitting ? "Updating..." : "Update password"}
        </motion.button>
      </form>
    </AuthSplitLayout>
  );
};

export default ResetPassword;